import { useState } from "react"
import { useNavigate } from "react-router-dom"
import AvatarStack from "@/components/AvatarStack/AvatarStack"
import Button from "@/components/Button/Button"
import LinkIcon from "@/components/LinkIcon/LinkIcon"
import FloatingBar, { BarDivider } from "@/components/FloatingBar/FloatingBar"
import Modal from "@/components/Modal/Modal"
import SessionSettings from "@/components/SessionSettings/SessionSettings"
import SessionTimer from "@/components/SessionTimer/SessionTimer"
import Spinner from "@/components/Spinner/Spinner"
import Tooltip from "@/components/Tooltip/Tooltip"
import { CARDS_COPY } from "@/content/cardsRound"
import { curtainFor } from "@/content/overlays"
import { PAINS_COPY } from "@/content/pains"
import {
  DEEP_DIVE_CTA,
  NAV_COPY,
  PRIORITIES_BLOCKED_CTA,
  ROUND_COPY,
  facilitatorCta,
  participantStatus,
} from "@/content/rounds"
import type { SessionView } from "@/hooks/useSessionView"
import { seedJobShortlist, startJob } from "@/services/jobs"
import { seedKnownPains } from "@/services/pains"
import { updateParticipant } from "@/services/participants"
import {
  completeSession,
  saveAndPause,
  setRoundAndPhase,
  showOverlay,
  startSession,
} from "@/services/sessions"
import { useExpiryTick } from "@/hooks/useExpiryTick"
import { useFirestoreWrite } from "@/hooks/useFirestoreWrite"
import { useTimerAlert, useWrapUpAlert } from "@/hooks/useTimerAlert"
import { useSessionStore } from "@/store/sessionStore"
import {
  jobHolds,
  nextPosition,
  painsBy,
  picksBy,
  previousPosition,
  selectAmIDone,
  selectCurrentDoneKey,
  selectJob,
  selectMyAuthorId,
  selectPains,
  selectPicks,
  selectSession,
  selectTimer,
  tallyPicks,
  unplacedCards,
  votesLeft,
} from "@/store/selectors"
import type { Position } from "@/store/selectors"
import type { FamilyNumber } from "@/theme/colors"
import { customerDetailPath, joinUrl } from "@/utils/routePaths"
import { isExpired } from "@/utils/sessionLifecycle"
import styles from "@/styles/components/RoundControls.module.css"

interface RoundControlsProps {
  canDrive: boolean
  /** Where this browser is looking. Owned by `Session` — see `useSessionView`. */
  view: SessionView
}

/** How many nominations the shortlist opens with. The prototype's top six. */
const SHORTLIST_SEED = 6

const FINISH_CTA = "Finish the session"
const SETTINGS = "Session settings"

/*
 * The two writes a button here waits on, tracked separately.
 *
 * Advancing is the longest write in the app — `prepareFor` can seed pain
 * documents, open a job and seed a shortlist before the position is even
 * written — and it is the one press a room of people is watching. One key each,
 * so pressing Back does not put a spinner on Continue.
 */
const ADVANCE = "advance"
const BACK = "back"
/** Parking the whole session for a follow-up call — its own key, so it never
 *  spins the Back/Continue buttons it sits beside. */
const SAVE_QUIT = "saveQuit"
/** How long the copy-link button reads "copied" before resetting. */
const COPIED_FOR_MS = 1800
/** Locking in a vote — the pains round's own write, tracked separately so it
 *  never spins the round's own Back/Continue buttons. */
const VOTE_DONE = "voteDone"
/** Marking your notes done during the write phase — same reasoning as
 *  `VOTE_DONE`, one key per phase's own write. */
const WRITE_DONE = "writeDone"
/** Marking your hand done during the pick phase — same reasoning again. */
const PICK_DONE = "pickDone"

/*
 * The bottom bar: who is in the room, the clock, the settings, and the one
 * button that moves everyone on.
 *
 * Advancing is deliberately concentrated here rather than spread across the
 * round screens. The rounds render state; this decides when the room may leave
 * one, and that decision has to be in one readable place — it is the rule set a
 * facilitator is actually operating.
 */
export default function RoundControls({ canDrive, view }: RoundControlsProps) {
  const session = useSessionStore(selectSession)
  const job = useSessionStore(selectJob)
  const pains = useSessionStore(selectPains)
  const picks = useSessionStore(selectPicks)
  const participants = useSessionStore((s) => s.participants)
  const timer = useSessionStore(selectTimer)
  const myAuthorId = useSessionStore(selectMyAuthorId)
  const amIDone = useSessionStore(selectAmIDone)
  const doneKey = useSessionStore(selectCurrentDoneKey)
  const currentParticipantId = useSessionStore((s) => s.currentParticipantId)
  const { write, isPending } = useFirestoreWrite()
  const navigate = useNavigate()
  const now = useExpiryTick([session?.expiresAt ?? null])
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [confirmingQuit, setConfirmingQuit] = useState(false)
  const [copied, setCopied] = useState(false)

  /*
   * Two rounds have no clock. Set-up has nothing to time — the room is arriving,
   * and a countdown over an empty table only makes the facilitator anxious — and
   * the debrief is a document being read, which finishes when it finishes.
   *
   * Computed before the early return below because the alert is a hook.
   */
  const timed = session !== null && session.round !== "lobby" && session.round !== "wrap"

  /*
   * The clock running out. Every screen in the room shakes, because the round
   * ending is the room's news; only the facilitator's rings, because theirs is
   * the machine on the speakers and eight phones dinging a second apart is a
   * noise the room has to recover from rather than a cue it can act on.
   */
  const timeUp = useTimerAlert(timed ? timer : null, canDrive)

  /*
   * Two minutes out, the bell rings early — see `useWrapUpAlert`.
   */
  const wrapUp = useWrapUpAlert(timed ? timer : null, canDrive)
  const timerBlock = (drives: boolean) => (
    <SessionTimer canDrive={drives} wrapUp={wrapUp.within} wrapUpAlerting={wrapUp.alerting} />
  )

  if (!session) return null

  const next = nextPosition(session)
  const previous = previousPosition(session)
  const finished = session.status === "completed"
  /*
   * Save & quit only shows while the session is live. A completed one is a record
   * and an expired one takes no write but extending its own deadline, so parking
   * either would be a button that fails into the toast — see `saveAndPause`.
   */
  const live = !finished && !isExpired(session, now)

  /*
   * Whether there is a voted problem to choose a job from — what `jobPick` puts
   * on screen. With the Pains round off there is no board at all, so there is
   * nothing to choose and nothing to ask.
   */
  const votedPains =
    session.config.painsEnabled !== false && pains.some((pain) => pain.voterIds.length > 0)

  // "Wrap up this job" is wrong when the dive is still to come — see DEEP_DIVE_CTA.
  const cta =
    session.round === "priorities" && next?.round === "deep"
      ? DEEP_DIVE_CTA
      : facilitatorCta(session.round, session.phase)

  /*
   * What has to be true before the room may leave this beat.
   *
   * Each of these is a gate the data model cannot enforce for itself — the
   * services take any write, and the rules cannot tell whether a job statement
   * reads well. Leaving without them produces a session that collects nothing:
   * a job with no sentence, a shortlist nobody picked from, a board with cards
   * still in the queue.
   */
  const blocked = (): string | null => {
    switch (session.round) {
      case "lobby":
        // The deck is what the room plays with, so there is nothing to start.
        return session.config.families.length === 0
          ? "Pick at least one card family to start"
          : null
      case "pains":
        if (session.phase === "write" && pains.length === 0) {
          return "Nothing has been written yet"
        }
        // The vote decides the job, so a tally with nothing in it has no winner.
        if (session.phase === "tally" && !pains.some((pain) => pain.voterIds.length > 0)) {
          return "No pain point has a vote yet"
        }
        return null
      case "job":
        return session.phase === "draft" && !jobHolds(job, session.config.userStoryEnabled)
          ? "The job needs a full sentence before the deck opens"
          : null
      case "cards":
        if (session.phase === "pick" && picks.every((pick) => pick.cardIds.length === 0)) {
          return "Nobody has picked a card yet"
        }
        if (session.phase === "review" && (job?.cardIds.length ?? 0) === 0) {
          return "Keep at least one card to take forward"
        }
        return null
      case "priorities": {
        const left = unplacedCards(job).length
        return left > 0 ? `${left} card${left === 1 ? "" : "s"} still to place` : null
      }
      case "deep":
      case "wrap":
        return null
    }
  }

  const blockedReason = canDrive ? blocked() : null

  /*
   * A nudge, not a gate.
   *
   * Revealing while the room is still heads-down defeats the round — the point
   * is that everyone writes alone — so the button says so for as long as nobody
   * has pressed "I'm done". It may not DISABLE the button, and that is the
   * whole reason it is separate from `blocked`: pressing "I'm done" is
   * optional, and needs a note written before it can be pressed at all, so a
   * room that simply stops writing and looks up would leave the facilitator
   * with no way to reveal at all. Who is still writing is in the rail beside
   * them — see `PainsMonitor` — and the call is theirs.
   */
  const hint =
    canDrive &&
    blockedReason === null &&
    session.round === "pains" &&
    session.phase === "write" &&
    participants.length > 0 &&
    !participants.some((participant) => participant.donePhase === doneKey)
      ? "Nobody has finished writing yet"
      : null

  /*
   * While the board still has a queue the button names the blocker instead of
   * the destination. A greyed-out "Deep dive" reads as broken; "Place every card
   * first" explains itself without the facilitator hunting for a tooltip.
   */
  const nextLabel = finished
    ? "The session is finished"
    : session.round === "priorities" && blockedReason !== null
      ? PRIORITIES_BLOCKED_CTA
      : (cta ?? (next ? "Continue" : FINISH_CTA))

  /*
   * What has to exist before the room can be at `position`.
   *
   * Separate from `goTo` because a round entered through a curtain never goes
   * through `goTo` — the overlay's own CTA performs that write. Both paths have
   * to prepare, so the preparation cannot live in either one of them.
   */
  const prepareFor = async (position: Position) => {
    if (position.round === "pains" && position.phase === "write") {
      /*
       * The room is about to vote on the facilitator's loaded notes, and a vote
       * is cast against a document. Idempotent, so stepping back and forward
       * again does not duplicate them.
       */
      await seedKnownPains(session.id)
    }

    /*
     * Everything from the Job round on hangs off a job, so one has to exist
     * before the room arrives — here, one with no pain behind it, because this is
     * the path taken when there is nothing to choose from: the Pains round is off
     * and the facilitator brings the problem themselves, or the tally somehow has
     * no votes in it.
     *
     * When the room DID vote, the job is opened by the `jobPick` overlay instead,
     * against the pain the facilitator chose. This used to reach into the tally
     * and convert the top pain itself, which decided for them — see `advance`.
     */
    if (position.round === "job" && !session.currentJobId) {
      await startJob(session.id, [])
    }

    /*
     * The shortlist opens as the room's own answer — the most-nominated cards —
     * and the facilitator drops from there. Seeded here rather than in the
     * screen so it is one write on the way in, not a render-time default that
     * every client would race to persist.
     */
    if (position.round === "cards" && position.phase === "review" && job) {
      const top = tallyPicks(picks)
        .slice(0, SHORTLIST_SEED)
        .map((nomination) => nomination.cardId)
      await seedJobShortlist(session.id, job.id, top)
    }
  }

  /** Moving the room on: one write, after whatever that position needs exists. */
  const goTo = async (position: Position) => {
    await prepareFor(position)

    if (session.status === "pending") {
      await startSession(session.id, position.round, position.phase)
      return
    }
    await setRoundAndPhase(session.id, position.round, position.phase)
  }

  /*
   * A completed session is a record, not a room: the rules refuse a position
   * write on one, so Back would fail into the toast rather than move anybody.
   * Reading it back is untouched — that goes through the header's view, which
   * writes nothing.
   */
  const back = finished
    ? null
    : previous
      ? () => write(() => goTo(previous), BACK)
      : session.round === "lobby"
        ? () => navigate(customerDetailPath(session.customerId))
        : null

  /*
   * A round the room enters through a curtain is raised, not walked into: the
   * overlay's own CTA then performs the move. So the advance either shows a
   * curtain or writes the position, never both — and `ROUND_ORDER` still decides
   * where the room is going, via `nextPosition`.
   *
   * Finishing a job is the same shape with a different curtain: the gate asks
   * whether to take another problem, and it owns the writes for both answers.
   */
  const advance = () =>
    write(async () => {
      if (!next) {
        await completeSession(session.id)
        /*
         * "Finish & save" hands the facilitator back their customer, where the
         * session now reads Complete. Staying on the report with a dead button
         * left them with nothing to do and no way out but the logo — and the
         * report is not lost: the row they land on opens it again.
         */
        navigate(customerDetailPath(session.customerId))
        return
      }

      /*
       * The job ends when the room's next stop is the write-up — from the board
       * with the deep dive switched off, or from the dive itself. Keyed on the
       * DESTINATION rather than the round being left, so turning the deep dive
       * on moves the gate to after it instead of raising it twice.
       */
      if (next.round === "wrap") {
        await showOverlay(session.id, "jobGate")
        return
      }

      /*
       * The first job is chosen, not assumed. The vote recommends a pain; the
       * room decides — a tie has no winner to convert, and a clear winner is
       * often set aside out loud. So the same screen the gate uses for the
       * second problem is raised for the first, and its CTA is what opens the
       * job.
       *
       * Guarded on there being something to choose: with the Pains round off, or
       * a tally nobody voted in, the list would be empty and its CTA permanently
       * disabled — the room stuck behind a curtain. That falls through to `goTo`,
       * where `prepareFor` opens an empty job as it always has. Guarded on
       * `currentJobId` too, so stepping Back into the tally and forward again
       * returns to the job already being worked instead of offering to fork it.
       */
      if (next.round === "job" && !session.currentJobId && votedPains) {
        await showOverlay(session.id, "jobPick")
        return
      }

      const curtain = next.round === session.round ? null : curtainFor(next.round)
      if (curtain) {
        // Prepared while the curtain goes up, so the room lands on a ready round
        // rather than on an empty one that fills in a moment later.
        await prepareFor(next)
        /*
         * The lobby is the one place raising a curtain also has to start the
         * session, or the room sits behind a curtain on a session still marked
         * pending — and nothing else writes `startedAt`.
         */
        if (session.status === "pending") {
          await startSession(session.id, session.round, session.phase)
        }
        await showOverlay(session.id, curtain)
        return
      }

      await goTo(next)
    }, ADVANCE)

  const advancing = isPending(ADVANCE)
  const goingBack = isPending(BACK)

  /*
   * Park the session and hand the facilitator back their customer — the same exit
   * as Finish & save, but the session stays resumable rather than closing. The
   * write marks it paused and pushes the deadline out; reopening the row drops the
   * room back where it stopped. Behind a confirm step because the button sits in a
   * row of presses the facilitator makes constantly and this one leaves the room.
   */
  const saveAndQuit = () =>
    write(async () => {
      await saveAndPause(session.id)
      navigate(customerDetailPath(session.customerId))
    }, SAVE_QUIT)
  const quitting = isPending(SAVE_QUIT)

  // The share link IS the session id — the same join URL the lobby hands out, and
  // the one the facilitator re-sends to gather the room for the follow-up call.
  const copyLink = () => {
    void navigator.clipboard?.writeText(joinUrl(session.id)).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), COPIED_FOR_MS)
    })
  }

  /*
   * Casting a vote happens on the note, not here — but locking the budget in
   * is the room's own control, and a participant has no bottom-bar CTA of
   * their own to share it with, the way the facilitator's copy of this card
   * shares one card with the token grid (see `PainsBoard`). So it rides here
   * instead, next to the timer they are already looking at.
   */
  const isVotingParticipant =
    !canDrive && !view.reviewing && session.round === "pains" && session.phase === "vote"
  const votesRemaining = votesLeft(pains, myAuthorId, session.config.votesPerParticipant)
  const toggleVoteDone = () => {
    if (!currentParticipantId) return
    write(
      () =>
        updateParticipant(session.id, currentParticipantId, {
          donePhase: amIDone ? null : doneKey,
        }),
      VOTE_DONE
    )
  }

  const isPickingParticipant =
    !canDrive && !view.reviewing && session.round === "cards" && session.phase === "pick"
  const cardsPicked = picksBy(picks, myAuthorId).length
  const togglePickDone = () => {
    if (!currentParticipantId) return
    write(
      () =>
        updateParticipant(session.id, currentParticipantId, {
          donePhase: amIDone ? null : doneKey,
        }),
      PICK_DONE
    )
  }

  const isWritingParticipant =
    !canDrive && !view.reviewing && session.round === "pains" && session.phase === "write"
  const painsWritten = painsBy(pains, myAuthorId).length
  const toggleWriteDone = () => {
    if (!currentParticipantId) return
    write(
      () =>
        updateParticipant(session.id, currentParticipantId, {
          donePhase: amIDone ? null : doneKey,
        }),
      WRITE_DONE
    )
  }

  /*
   * A screen that is behind the room does not get the controls that move it.
   *
   * Not because the write would fail — it would land perfectly well — but
   * because it names a destination computed from `session.round`, and the
   * facilitator reading it is looking at something else entirely. "Open the
   * deck" pressed while re-reading the pain points would move the whole room
   * out of a round the presser cannot see. So the bar says where the room is and
   * offers the way back to it, and the timer stays, because the clock is running
   * whether or not you are watching the round it belongs to.
   */
  if (view.reviewing) {
    return (
      <FloatingBar alert={timeUp}>
        {/* The clock keeps its controls. It belongs to the round the ROOM is in,
         * not to the one on screen — a facilitator who glanced back at the pain
         * points should not have to come forward again to add a minute to the
         * round everyone is actually working. */}
        {timed ? (
          <>
            {timerBlock(canDrive)}
            <BarDivider />
          </>
        ) : null}
        <span className={styles.waiting}>
          <span className={styles.dot} aria-hidden="true" />
          {NAV_COPY.roomIsOn(ROUND_COPY[session.round].label)}
        </span>
        <button type="button" className={styles.quiet} onClick={view.live}>
          {NAV_COPY.backToLive}
        </button>
      </FloatingBar>
    )
  }

  return (
    <>
      <FloatingBar alert={timeUp}>
        {canDrive ? (
          <>
            {/* The room, so the facilitator never has to leave the round to
             * see who is still in it. */}
            {participants.length > 0 ? (
              <>
                <AvatarStack
                  people={participants.map((participant, index) => ({
                    ...participant,
                    family: ((index % 7) + 1) as FamilyNumber,
                  }))}
                  size={28}
                  max={7}
                />
                <BarDivider />
              </>
            ) : null}

            {timed ? (
              <>
                {timerBlock(true)}
                <BarDivider />
              </>
            ) : null}

            {/* Same reason as Back: every control in the settings panel is a
             * write into a session that no longer takes them. */}
            {finished ? null : (
              <>
                <Tooltip label={SETTINGS} placement="top" delay={60}>
                  <button
                    type="button"
                    className={styles.icon}
                    aria-label={SETTINGS}
                    onClick={() => setSettingsOpen(true)}
                  >
                    {/* A toothed cog — the old circle-with-rays read as a brightness
                     * or dark-mode glyph, not settings. */}
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.9"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                </Tooltip>
                {/* The join link, one tap to copy — needed to gather the room
                 * again when a saved session is resumed. The full link box lives
                 * in Session settings; this is the quick grab. */}
                <Tooltip
                  label={copied ? NAV_COPY.copiedLink : NAV_COPY.copyLink}
                  placement="top"
                  delay={60}
                >
                  <button
                    type="button"
                    className={styles.icon}
                    aria-label={copied ? NAV_COPY.copiedLink : NAV_COPY.copyLink}
                    onClick={copyLink}
                  >
                    <LinkIcon done={copied} />
                  </button>
                </Tooltip>
                <BarDivider />
              </>
            )}

            {/* Parking the session sits before Back: it is the way out that keeps
             * the room, where Finish & save (the Continue button on the wrap) is
             * the way out that closes it. Disabled while a position write is out,
             * so a pause cannot race an advance. */}
            {live ? (
              <button
                type="button"
                className={styles.quiet}
                title={NAV_COPY.saveAndQuitHint}
                disabled={quitting || advancing || goingBack}
                onClick={() => setConfirmingQuit(true)}
              >
                {NAV_COPY.saveAndQuit}
              </button>
            ) : null}

            {back ? (
              <button
                type="button"
                className={styles.quiet}
                /* Both directions are the same write, so neither may be
                 * pressed while the other is out: two positions landing in an
                 * unknown order would leave the room somewhere neither press
                 * asked for. */
                disabled={goingBack || advancing}
                aria-busy={goingBack || undefined}
                onClick={back}
              >
                {goingBack ? <Spinner size="sm" /> : null}
                Back
              </button>
            ) : null}
            <button
              type="button"
              className={styles.button}
              disabled={finished || blockedReason !== null || advancing || goingBack}
              title={blockedReason ?? hint ?? undefined}
              aria-busy={advancing || undefined}
              onClick={advance}
            >
              {advancing ? <Spinner /> : null}
              {nextLabel}
              <span className={styles.arrow} aria-hidden="true">
                →
              </span>
            </button>
          </>
        ) : (
          <>
            {timed ? (
              <>
                {timerBlock(false)}
                <BarDivider />
              </>
            ) : null}
            {isVotingParticipant ? (
              <>
                <span className={`${styles.waiting} ${styles.votesStatus}`}>
                  <span className={styles.dot} aria-hidden="true" />
                  {PAINS_COPY.budget(votesRemaining)}
                </span>
                {currentParticipantId
                  ? (() => {
                      const doneButton = (
                        <button
                          type="button"
                          className={amIDone ? `${styles.done} ${styles.doneOn}` : styles.done}
                          aria-pressed={amIDone}
                          // The bubble carries the full sentence, so it must not be
                          // announced twice — see `Tooltip`.
                          aria-label={amIDone ? PAINS_COPY.votesLockedTooltip : undefined}
                          /*
                           * `aria-disabled`, never `disabled`, once locked in: a
                           * native disabled button cannot receive focus, so the
                           * tooltip explaining WHY — this button's only
                           * explanation — would never reach a keyboard user. See
                           * `SessionHeader`'s rail pill for the same trade. The
                           * other two reasons keep a real `disabled`; neither has
                           * anything to explain.
                           */
                          disabled={
                            !amIDone &&
                            (votesRemaining === session.config.votesPerParticipant ||
                              isPending(VOTE_DONE))
                          }
                          aria-disabled={amIDone || undefined}
                          aria-busy={isPending(VOTE_DONE) || undefined}
                          onClick={() => {
                            if (amIDone) return
                            toggleVoteDone()
                          }}
                        >
                          {isPending(VOTE_DONE) ? <Spinner size="sm" /> : null}
                          {amIDone ? PAINS_COPY.lockedIn : PAINS_COPY.imDone}
                        </button>
                      )
                      return amIDone ? (
                        <Tooltip label={PAINS_COPY.votesLockedTooltip} placement="top">
                          {doneButton}
                        </Tooltip>
                      ) : (
                        doneButton
                      )
                    })()
                  : null}
              </>
            ) : isWritingParticipant ? (
              <>
                <span className={`${styles.waiting} ${styles.votesStatus}`}>
                  <span className={styles.dot} aria-hidden="true" />
                  {PAINS_COPY.brief(painsWritten, session.config.painsPerParticipant)}
                </span>
                {currentParticipantId ? (
                  <button
                    type="button"
                    className={amIDone ? `${styles.done} ${styles.doneOn}` : styles.done}
                    aria-pressed={amIDone}
                    disabled={painsWritten === 0 || isPending(WRITE_DONE)}
                    aria-busy={isPending(WRITE_DONE) || undefined}
                    onClick={toggleWriteDone}
                  >
                    {isPending(WRITE_DONE) ? <Spinner size="sm" /> : null}
                    {amIDone ? PAINS_COPY.imDoneWaiting : PAINS_COPY.imDone}
                  </button>
                ) : null}
              </>
            ) : isPickingParticipant ? (
              <>
                <span className={styles.waiting}>
                  <span className={styles.dot} aria-hidden="true" />
                  {participantStatus(session.round, session.phase)}
                </span>
                {currentParticipantId ? (
                  <button
                    type="button"
                    className={amIDone ? `${styles.done} ${styles.doneOn}` : styles.done}
                    aria-pressed={amIDone}
                    disabled={cardsPicked === 0 || isPending(PICK_DONE)}
                    aria-busy={isPending(PICK_DONE) || undefined}
                    onClick={togglePickDone}
                  >
                    {isPending(PICK_DONE) ? <Spinner size="sm" /> : null}
                    {amIDone ? CARDS_COPY.imDoneWaiting : CARDS_COPY.imDone}
                  </button>
                ) : null}
              </>
            ) : (
              <span className={styles.waiting}>
                <span className={styles.dot} aria-hidden="true" />
                {participantStatus(session.round, session.phase)}
              </span>
            )}
          </>
        )}
      </FloatingBar>

      {settingsOpen ? <SessionSettings onClose={() => setSettingsOpen(false)} /> : null}

      {confirmingQuit ? (
        <Modal
          title={NAV_COPY.quitTitle}
          width="narrow"
          dismissible
          onClose={() => setConfirmingQuit(false)}
          actions={
            <>
              <Button type="button" variant="quiet" onClick={() => setConfirmingQuit(false)}>
                {NAV_COPY.quitCancel}
              </Button>
              <Button type="button" onClick={saveAndQuit} loading={quitting}>
                {NAV_COPY.quitConfirm}
              </Button>
            </>
          }
        >
          <p className={styles.quitNote}>{NAV_COPY.quitBody}</p>
        </Modal>
      ) : null}
    </>
  )
}
